import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  Chip,
} from '@mui/material';
import { NavigateNext, Home } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const PageHeader = ({ 
  title, 
  subtitle, 
  description, 
  tags = [], 
  showBreadcrumbs = true,
  backgroundImage,
  gradient = true 
}) => {
  const location = useLocation();
  
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    
    const breadcrumbItems = [
      <Link
        key="home"
        component={RouterLink}
        to="/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'grey.300',
          textDecoration: 'none',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Home sx={{ mr: 0.5, fontSize: 18 }} />
        Home
      </Link>
    ];

    pathnames.forEach((name, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      
      const displayName = name
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (isLast) {
        breadcrumbItems.push(
          <Typography key={name} sx={{ color: 'white', fontWeight: 600 }}>
            {displayName}
          </Typography>
        );
      } else {
        breadcrumbItems.push(
          <Link
            key={name}
            component={RouterLink}
            to={routeTo}
            sx={{
              color: 'grey.300',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {displayName}
          </Link>
        );
      }
    });

    return breadcrumbItems;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        background: gradient 
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #2d2d2d 100%)'
          : 'background.default',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: backgroundImage ? 'overlay' : 'normal',
        borderBottom: '1px solid',
        borderColor: 'rgba(255, 107, 53, 0.1)',
      }}
    >
      {/* Overlay for background image */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 15, 15, 0.8)',
            zIndex: 1,
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {showBreadcrumbs && (
          <Breadcrumbs
            separator={<NavigateNext sx={{ color: 'grey.500' }} />}
            sx={{ mb: 3 }}
          >
            {generateBreadcrumbs()}
          </Breadcrumbs>
        )}

        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          {subtitle && (
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              {subtitle}
            </Typography>
          )}

          <Typography
            variant="h1"
            component="h1"
            sx={{
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              background: 'linear-gradient(45deg, #ff6b35, #4ecdc4)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="h6"
              sx={{
                color: 'grey.300',
                lineHeight: 1.6,
                mb: 4,
                fontWeight: 400,
              }}
            >
              {description}
            </Typography>
          )}

          {tags.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ gap: 1 }}
            >
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  color={index % 2 === 0 ? 'primary' : 'secondary'}
                  variant={index % 2 === 0 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PageHeader;